/**
 * @file apps/web/src/pages/portal/PortalFDDPage.tsx
 * @description Client portal documents — read-only FDD/BRD viewer.
 * Clients can read all document sections but cannot edit or regenerate.
 * Route: /portal/fdd  (optionally ?doc=<documentId>, which opens that document —
 * used by the "View document" link on an approval card; &from=approvals adds a
 * link back to Approvals).
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FileText, ChevronRight, AlertCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { usePortalAuth } from '../../lib/portalAuth.js';
import { documentsApi } from '../../lib/api.js';
import { Badge } from '../../components/Badge.js';
import { documentTypeLabel } from '../../lib/documentLabels.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Document {
  id:         string;
  title:      string;
  doc_type:   string;
  status:     string;
  created_at: string;
  updated_at: string;
}

interface FDDSection {
  section_key:  string;
  section_title?: string | null;
  content_md?:  string | null;
  order_index?: number | null;
}

// ---------------------------------------------------------------------------
// Section block (read-only rendered markdown)
// ---------------------------------------------------------------------------

function SectionBlock({ section }: { section: FDDSection }) {
  const [expanded, setExpanded] = useState(true);
  const hasContent = !!section.content_md?.trim();

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-sky/20 transition-colors"
      >
        <p className="text-sm font-semibold text-ink">
          {section.section_title ?? section.section_key.replace(/_/g, ' ')}
        </p>
        {expanded
          ? <ChevronUp size={14} className="text-muted" />
          : <ChevronDown size={14} className="text-muted" />
        }
      </button>

      {expanded && (
        <div className="border-t border-[rgba(8,17,31,0.06)] px-4 py-3">
          {hasContent ? (
            // content_md is HTML (sanitized server-side before storage — see
            // apps/api/src/lib/markdown.ts) written by the same Tiptap editor
            // consultants use, so it's rendered directly rather than
            // re-parsed as markdown.
            <div
              className="prose prose-sm max-w-none text-ink/80 leading-relaxed"
              style={{ fontSize: '0.875rem' }}
              dangerouslySetInnerHTML={{ __html: section.content_md! }}
            />
          ) : (
            <p className="text-sm text-muted/60 italic">
              This section has not been filled in yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PortalFDDPage
// ---------------------------------------------------------------------------

export function PortalFDDPage() {
  const { user, devHeaders } = usePortalAuth();

  const [docs, setDocs]           = useState<Document[]>([]);
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [sections, setSections]   = useState<FDDSection[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [error, setError]         = useState('');
  const [searchParams] = useSearchParams();
  const requestedDocId = searchParams.get('doc');
  const cameFromApprovals = searchParams.get('from') === 'approvals';

  // Load documents
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setDocsLoading(true);
    documentsApi.list(user.projectId, devHeaders)
      .then((data) => {
        if (!cancelled) {
          const d = data as Document[];
          setDocs(d);
          // Open the document named in ?doc= when it's there, else the first.
          const wanted = requestedDocId ? d.find((x) => x.id === requestedDocId) : undefined;
          if (d.length > 0) setActiveDocId((wanted ?? d[0]!).id);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load documents');
      })
      .finally(() => { if (!cancelled) setDocsLoading(false); });
    return () => { cancelled = true; };
  }, [user?.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load sections when active doc changes
  useEffect(() => {
    if (!activeDocId) return;
    let cancelled = false;
    setSectionsLoading(true);
    documentsApi.getSections(activeDocId, devHeaders)
      .then((data) => { if (!cancelled) setSections(data as FDDSection[]); })
      .catch(() => { if (!cancelled) setSections([]); })
      .finally(() => { if (!cancelled) setSectionsLoading(false); });
    return () => { cancelled = true; };
  }, [activeDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeDoc = docs.find((d) => d.id === activeDocId);
  const sortedSections = [...sections].sort(
    (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)
  );

  return (
    <div className="p-8 max-w-4xl">
      {cameFromApprovals && (
        <Link to="/portal/approvals" className="inline-flex items-center gap-1 text-xs text-blue font-medium hover:underline mb-3">
          ← Back to Approvals
        </Link>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="page-title">Documents</h1>
        <p className="text-sm text-muted mt-1">
          View your project's business requirements and functional design documents.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {docsLoading ? (
        <div className="card h-64 animate-pulse bg-gray/60" />
      ) : docs.length === 0 ? (
        <div className="empty-state py-16">
          <FileText size={32} className="text-muted/30 mb-3 mx-auto" />
          <p className="text-sm text-muted">No documents available yet.</p>
          <p className="text-xs text-muted/70 mt-1">
            Your implementation team is still preparing your documents.
          </p>
        </div>
      ) : (
        <div className="flex gap-5 items-start">
          {/* Doc list sidebar */}
          {docs.length > 1 && (
            <aside className="w-48 shrink-0 space-y-1">
              <p className="section-label px-1 mb-2">Documents</p>
              {docs.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => setActiveDocId(doc.id)}
                  className={[
                    'w-full text-left p-2.5 rounded-lg border text-sm transition-colors',
                    doc.id === activeDocId
                      ? 'border-blue bg-sky text-blue'
                      : 'border-[rgba(8,17,31,0.08)] hover:border-blue/30 hover:bg-sky/20 text-ink',
                  ].join(' ')}
                >
                  <div className="flex items-center justify-between gap-1">
                    <p className="font-medium truncate text-[13px]">{doc.title}</p>
                    {doc.id === activeDocId && <ChevronRight size={11} className="text-blue shrink-0" />}
                  </div>
                  <p className="text-[10px] font-mono text-muted mt-0.5">{documentTypeLabel(doc.doc_type)}</p>
                </button>
              ))}
            </aside>
          )}

          {/* Document content */}
          <div className="flex-1 min-w-0 space-y-3">
            {activeDoc && (
              <div className="flex items-center gap-2 mb-4">
                <h2 className="font-display text-lg font-semibold text-ink">{activeDoc.title}</h2>
                <Badge variant={activeDoc.status as never} size="xs" />
                <span className="text-[11px] font-mono text-muted">
                  {documentTypeLabel(activeDoc.doc_type)}
                </span>
              </div>
            )}

            {sectionsLoading ? (
              <div className="space-y-2 animate-pulse">
                {[1,2,3].map((i) => <div key={i} className="card h-16 bg-gray/60" />)}
              </div>
            ) : sortedSections.length === 0 ? (
              <div className="empty-state py-10">
                <FileText size={24} className="text-muted/40 mb-2 mx-auto" />
                <p className="text-sm text-muted">No sections available for this document.</p>
              </div>
            ) : (
              sortedSections.map((section) => (
                <SectionBlock key={section.section_key} section={section} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
